/**
 * @file 请求包装
 * @author  fangsimin
 * @date    2019-01-14 15:14:23
 * @last Modified by    fangsimin
 * @last Modified time  2019-01-14 20:02:45
 */

import axios from 'axios';
import qs from 'qs';
import axiosRetry from 'axios-retry';
import { captrueApiException } from '@/plugins/weirwood';
import Cookies from 'js-cookie';
import { enqueue, cancelDuplicate } from '@/common/requestQueue';
import { getTimeoutDetail, collectRequestMetrics } from '@/common/requestMetrics';

// ========== axios-retry 全局配置 ==========
// 默认不重试（retries: 0），业务侧按需通过 opts.retry 开启
axiosRetry(axios, {
    retries: 0,
    shouldResetTimeout: true,
    retryDelay: axiosRetry.exponentialDelay,
    retryCondition(error) {
        return axiosRetry.isNetworkOrIdempotentRequestError(error)
            || (error.code === 'ECONNABORTED');
    }
});

// 灰度发布标记
const handleIsgu = (options) => {
    const isgu = Cookies.get('isgu');
    if (isgu !== undefined && options['headers']) {
        options['headers'].isgu = isgu;
    }
};

const reqQueue = {};
const addRequestQueue = (config) => {
    const {url} = config;
    if(reqQueue[url]) {
        reqQueue[url].abort();
        delete reqQueue[url];
    }
    const controller = new AbortController();
    config.signal = controller.signal;
    reqQueue[url] = controller;
};
axios.interceptors.request.use(config => {
    let url = config.url;
    // get参数编码
    if ((config.method === 'get' || config.method === 'delete') && config.params) {
        // isThrottle get请求节流标记
        config.isThrottle && addRequestQueue(config);
        url += '?';
        let keys = Object.keys(config.params);
        for (let key of keys) {
            if (config.params[key] !== undefined) {
                url += `${key}=${encodeURIComponent(config.params[key])}&`;
            }
        }
        url = url.substring(0, url.length - 1);
        config.params = {};
    }
    config.url = url;
    return config;
});

/**
 * 请求失败时的初步处理回调
 * @param  {Object} response 返回
 * @param  {number} [callTime] 请求发起时间戳，用于精确计算排队/各阶段耗时
 * @return  {Object} response 返回
 */
function errorState(response, callTime) {
    try {
        const code = response && response.data && response.data.code;
        const status = response && response.status;
        const codes = response && response.code && response.code;
        const isEffectiveResponse = +status === 200 && (+code === 0 || +code === 101);
        if (codes === 'ECONNABORTED') {
            const timeoutDetail = getTimeoutDetail(response, callTime);
            if (timeoutDetail && response.config) {
                response.config.timeoutDetail = timeoutDetail;
                // 超时根因为排队阻塞时，在 response 上标记，方便业务层区分处理
                if (timeoutDetail.timeoutType === 'QUEUE_TIMEOUT') {
                    response.config.isQueueTimeout = true;
                }
            }
        }
        if (!isEffectiveResponse || codes === 'ECONNABORTED') {
            response && captrueApiException(response);
        }
    }
    catch (err) {
        console.log(err);
    }
    if (response && (response.status === 200 || response.status === 304 || response.status === 400)) {
        return response;
    } else if (response && +response.status === 401) {
        const url = (response.headers && response.headers['location']) || '';
        if (url) {
            location.href = url;
        }
        return response ? response : {};
    } else {
        return response ? response : {};
    }
}

/**
 * 请求成功返回时的初步处理回调
 * @param  {Object} response 返回
 * @return {boolean} 返回服务端的code是否为0
 */
function successState(response) {
    // 101状态码用于处理登录失败的特殊提示
    if (response.data && (+response.data.code === 0 || +response.data.code === 101)) {
        return true;
    } else {
        return false;
    }
    // 一判断后端返回的错误码
    // if (res.data.errCode == '000002') {
    //   console.log(res.data.errDesc || '网络异常')
    // } else if (res.data.errCode != '000002' && res.data.errCode != '000000') {
    //   console.log(res.data.errDesc || '网络异常')
    // }
}

/**
 * 文件下载
 * @param {ArrayBuffer} response
 */
function downloadFile(data, fileName, type) {
    const url = window.URL.createObjectURL(new Blob([data], { type: type }));
    const link = document.createElement('a');
    link.style.display = 'none';
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

/**
 * 组装 axios config 的公共逻辑（从 opts + data 构建 httpDefaultOpts）
 * 抽取为独立函数，便于队列和非队列路径复用
 */
function buildHttpConfig(opts, data) {
    if (!opts.method) {
        opts.method = 'get';
    }
    let defaultParam = {};
    let httpDefaultOpts = {
        baseURL: opts.baseURL,
        method: opts.method,
        url: opts.url,
        timeout: opts.timeout || 30000,
        isThrottle: opts.isThrottle
    };

    // ---- axios-retry 按接口配置 ----
    if (opts.retry) {
        httpDefaultOpts['axios-retry'] = {
            retries: opts.retryCount || 2,
            shouldResetTimeout: true
        };
        if (typeof opts.retryCondition === 'function') {
            httpDefaultOpts['axios-retry'].retryCondition = opts.retryCondition;
        }
    }

    const randomString = [...Array(16)].map(() => Math.random().toString(36)[2]).join('');
    if (opts.method === 'get' || opts.method === 'delete') {
        httpDefaultOpts['params']
            = Object.assign(defaultParam, opts.params, data);
        httpDefaultOpts['headers'] = {
            Accept: 'application/json',
            'Content-Type': 'application/json; charset=UTF-8',
            Traceid: randomString
        };
    } else {
        if (opts['ajaxType'] && opts['ajaxType'] === 'upload') {
            httpDefaultOpts['data'] = data;
        } else {
            httpDefaultOpts['data']
                = opts.qs
                    ? qs.stringify(Object.assign(defaultParam, opts.data, data))
                    : Object.assign(defaultParam, opts.data, data);
        }
        httpDefaultOpts['headers'] = {
            'Content-Type': opts.contentType || 'application/x-www-form-urlencoded; charset=UTF-8',
            Traceid: randomString
        };
        httpDefaultOpts['transformRequest'] = opts.contentType ? opts.transformRequest : [function (data) {
            let ret = '';
            /* eslint-disable-next-line */
            for (let it in data) {
                ret += encodeURIComponent(it) + '=' + encodeURIComponent(data[it]) + '&';
            }
            return ret;
        }];
    }
    handleIsgu(httpDefaultOpts);
    if (opts.responseType === 'blob' || opts.responseType === 'text') {
        httpDefaultOpts['responseType'] = opts.responseType;
        if (opts.method === 'get' || opts.method === 'delete') {
            httpDefaultOpts['headers']['Accept'] = opts.responseType === 'text' ? 'text/plain, text/markdown, */*' : '*/*';
        }
    }

    // 防抖 key：支持自定义，否则用 method:url
    if (opts.dedupKey) {
        httpDefaultOpts.dedupKey = opts.dedupKey;
    }

    return httpDefaultOpts;
}

/**
 * 执行实际的 axios 请求并处理响应
 * @param {Object} httpDefaultOpts - 构建好的 axios config
 * @param {Object} opts - 原始调用参数（用于读取 responseType/startTime 等）
 * @returns {Promise}
 */
function executeRequest(httpDefaultOpts, opts) {
    const callTime = Date.now();
    opts.startTime = callTime;

    return new Promise(function (resolve, reject) {
        axios(httpDefaultOpts).then(response => {
            if ((opts.responseType === 'blob' || opts.responseType === 'text') && +response.status === 200) {
                resolve(response.data);
                return;
            }
            response.data.wait_seconds = (Date.now() - callTime) / 1000;

            // 成功请求仅在 data 上挂性能指标供调试，不触发 weirwood 上报
            const metrics = collectRequestMetrics(response.config || httpDefaultOpts, callTime);
            if (metrics) {
                response.data._requestMetrics = metrics;
            }

            if (successState(response)) {
                resolve(response.data);
            } else {
                errorState(response, callTime);
                reject(response);
            }
        }).catch(response => {
            if (axios.isCancel(response)) return;
            const res = response && response.response ? response.response : response;
            errorState(res, callTime);
            reject(res);
        });
    });
}

export default (opts, data) => {
    const httpDefaultOpts = buildHttpConfig(opts, data);

    // ---- 下载请求：不入队列，保持原有逻辑 ----
    if (opts.type === 'download') {
        httpDefaultOpts['responseType'] = 'arraybuffer';
        return new Promise((resolve, reject) => {
            axios(httpDefaultOpts).then(response => {
                if (+response.status === 200) {
                    const header = response.headers;
                    let fileName = (
                        header['content-disposition']
                        && header['content-disposition'].split('filename=')[1]
                    ) || 'filename';
                    fileName = fileName.replace(/^["'_]+|["'_]+$/g, '');
                    downloadFile(response.data, fileName, opts.fileType);
                    resolve(true);
                } else {
                    if (+response.status === 401) {
                        this.$message.error(this.$i18n.t('message.permissionDenied'));
                        setTimeout(() => {
                            window.location.replace('/adx-dc/logout.do');
                        }, 500);
                    }
                    reject(response);
                }
            }).catch(response => {
                errorState(response);
                reject(response);
            });
        });
    }

    if (opts.responseType !== 'blob' && opts.responseType !== 'text') {
        httpDefaultOpts['responseType'] = 'json';
    }

    // ---- 跳过队列的请求（如某些必须立刻发出的关键接口） ----
    if (opts.skipQueue) {
        return executeRequest(httpDefaultOpts, opts);
    }

    // ---- 普通请求：通过并发队列控制 ----
    const priority = opts.priority || 0;

    return enqueue(() => {
        // 同接口防抖：isThrottle 开启时取消同 URL 旧请求
        let dedupCleanup;
        if (opts.isThrottle) {
            dedupCleanup = cancelDuplicate(httpDefaultOpts);
        }

        return executeRequest(httpDefaultOpts, opts).finally(() => {
            if (dedupCleanup) {
                dedupCleanup();
            }
        });
    }, { priority });
};
